<i18n>
{
	"en": {
		"customAddress": "Custom Address",
		"edit": "Edit",
		"failedToFetch": "Failed to search for addresses",
		"position": "Position"
	},
	"no": {
		"customAddress": "Ny adresse",
		"edit": "Rediger",
		"failedToFetch": "Kunne ikke søke etter adresser",
		"position":"Posisjon"
	}
}
</i18n>

<template>
	<div>
		<t-input-card
			:header="title"
			:title="value.street"
			:text="`${value.postalCode || ''} ${value.postalPlace || ''}`"
		>
			<template #header-after>
				<slot name="header" />
			</template>

			<template #input>
				<t-combobox-button
					ref="input"
					icon="geosearch"
					:limit="$options.SERVER_RETURN_LIMIT"

					:searchValue="searchValue"
					:items="filteredItems"
					:selected="value"

					:disabled="disabled"
					disableButtonTabbing

					@focus="onFocus"
					@input="onInput"
					@blur="onBlur"
					@change="onChange"
					@button:click="setShowDialog(true)"
				>
					<template #optionItemContent="{ item }">
						<div class="item">
							<div class="item-main-text">{{ item.name }}</div>
							<div class="item-sub-text">{{ item.street }}, {{ item.postalCode }} {{ item.postalPlace }}</div>
						</div>
					</template>
				</t-combobox-button>
			</template>

			<template #title-after>

				<div class="title-buttons">
					<t-icon-button
						:title="$t('edit')"
						icon="edit"
						type="bp"
						appearance="minimal"
						disableBoxShadow
						:tabindex="-1"
						@click.prevent="onAddressEdit(value)"
					/>
					<t-icon-button
						:title="$t('position')"
						icon="map-marker"
						type="bp"
						appearance="minimal"
						disableBoxShadow
						:disabled="!value.latitude || !value.longitude"
						:tabindex="-1"
						@click="openPositionInGoogleMaps(value)"
					/>
				</div>
			</template>
		</t-input-card>

		<global-addresses-form-input-dialog
			:isShown="showDialog"
			:initialSearchValue="dialogInitialSearchValue"
			icon="geosearch"
			@close="setShowDialog(false)"
			@ok="onChange"
		/>
		<edit-global-address-dialog
			:address="editAddress"
			:isShown="showEditAddressDialog"
			@close="showEditAddressDialog = false"
			@ok="onChange"
		/>
	</div>
</template>

<script>
import { mapActions } from './store/globalAddresses.store.js';
import GlobalAddressesFormInputDialog from './GlobalAddressInput/GlobalAddressDialog';
import EditGlobalAddressDialog from './GlobalAddressInput/EditGlobalAddressDialog';
import TIconButton from '@/components/evergreen/buttons/TIconButton';
import TComboboxButton from '@/components/evergreen/timpex/inputs/TComboboxButton';

export default {
	name: 'GlobalAddressInput',
	SERVER_RETURN_LIMIT: 50,
	components: { TComboboxButton, TIconButton, GlobalAddressesFormInputDialog, EditGlobalAddressDialog },
	props: {
		value: Object,
		title: String,
		disabled: {
			type: Boolean,
			default: false
		},
		focusNextOnSelected: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			items: [],
			showDialog: false,
			showEditAddressDialog: false,
			editAddress: {},
			searchValue: '',
			lastSearch: null,
			searchTimeout: null,
			dirty: false,
			focused: false,
		};
	},
	computed: {
		dialogInitialSearchValue() {
			return this.dirty
				? this.searchValue
				: '';
		},
		filteredItems() {
			if (this.searchValue === '')
				return this.items;

			return this.items.filter( item => {

				if (!item)
					return false;

				let loweredSearchValue = this.searchValue.toLowerCase();

				let ret = true;
				for (let c of loweredSearchValue.split(' ')) {
					ret = ret &&
						( (item.postalPlace && item.postalPlace.toLowerCase().includes(c))
							|| ( item.postalCode && item.postalCode.toString().toLowerCase().includes(c) )
							|| ( item.street && item.street.toLowerCase().includes(c))
							|| ( item.name && item.name.toLowerCase().includes(c))
						);
				}
				return ret;
			});
		}
	},
	watch: {
		value: {
			immediate: true,

			handler(value) {
				this.searchValue = this.getSearchValueFromValue(value);
			}
		}
	},
	methods: {
		...mapActions(['fetchFilteredGlobalAddresses']),
		resetInput() {
			this.searchValue = this.getSearchValueFromValue(this.value);
		},

		onFocus() {
			this.focused = true;
		},
		onInput(searchValue) {
			this.dirty = true;

			this.searchValue = searchValue;

			if (this.shouldCancelNewSearch(searchValue, this.lastSearch, this.items))
				return;

			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {

				this.search(searchValue);

			}, 250);
		},
		shouldCancelNewSearch(searchValue, lastSearch, items) {
			return (
				searchValue === ''
				|| (
					items.length < this.$options.SERVER_RETURN_LIMIT
					&& searchValue.startsWith( lastSearch )
				)
			);
		},
		onChange(value) {

			this.searchValue = this.getSearchValueFromValue(value);

			this.setShowDialog(false);
			this.showEditAddressDialog = false;

			this.$emit('change', value);

			if (
				this.focusNextOnSelected
				&& Object.keys(value).length
			) {
				window.focusNext({ target: this.$refs.input.$el.querySelector('input') });
			}
			this.dirty = false;
		},
		onBlur(event) {
			this.focused = false;

			if (this.isRelatedTargetTheComboboxButton(event))
				return;

			if (
				this.value
				&& ( this.value.id || this.value.name || this.value.postalPlace || this.value.postalCode )
				&& this.searchValue === ''
			)
				this.$emit('change', {});

			else
				this.searchValue = this.getSearchValueFromValue(this.value);
		},
		isRelatedTargetTheComboboxButton(event) {

			return event.relatedTarget === this.$refs.input.$el.querySelector('button');

		},
		async search(searchValue) {
			try {
				this.items = await this.fetchFilteredGlobalAddresses({ filterString: searchValue });

				if (
					!this.focused
					&& this.items
					&& this.items.length === 1
				) {
					this.$emit('change', this.items[0]);
					this.searchValue = this.getSearchValueFromValue(this.items[0]);
				}
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
			this.lastSearch = searchValue;
		},
		getSearchValueFromValue(value) {

			if (value && value.name && value.name.length)
				return value.name;
			else
				return '';
		},
		onAddressEdit(address) {
			this.editAddress = address;
			this.showEditAddressDialog = true;
		},
		openPositionInGoogleMaps(data) {
			let url = `https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`;
			let win = window.open(url, '_blank');
			win.focus();
		},
		setShowDialog(showDialog) {

			if (this.showDialog === true && showDialog === false)
				this.searchValue = this.getSearchValueFromValue(this.value);

			this.showDialog = showDialog;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.item {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
}

.item-main-text {
	height:16px;
	line-height: 16px;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
}

.item-sub-text {
	height:16px;
	font-size: 10px;
	font-weight: normal;
	font-style: normal;
	font-stretch: normal;
	line-height: 1.6;
	letter-spacing: normal;
	overflow: hidden;
	white-space: nowrap;
}

.details {
	@extend %text-small;
	color: get-color($text, 'muted');
	padding-left: 10px;
}

.title-buttons {
	display: flex
}
</style>
